import React, {useContext, useEffect, useMemo, useState} from "react";
import Paper from '@mui/material/Paper';
import Grid from "@mui/material/Grid";
import TransactionInput from "../Transactions/TransactionInput/TransactionInput";
import {styled} from '@mui/material/styles'
import formatters from "../../utils/formatter";
import {appConfig} from "../../utils/settings";
import {Box, Fab, Tooltip as MUITooltip} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {AccountContext} from "../../context/accountContext";
import {UserContext} from "../../context/userContext";
import Typography from "@mui/material/Typography";
import DateYearSelection from "./DateYearSelection";
import AppDrawer from "../AppDrawer/AppDrawer";
import {LineChart} from "@mui/x-charts/LineChart";
import {AuthContext} from "../../context/authContext";
import BudgetStatus from "../shared/BudgetStatus";
import {PieChart} from "@mui/x-charts";
import TransactionsByUser from "../shared/TransactionsByUser";
import CategorySpendBarChart from "../shared/MetricPanel/CategorySpendBarChart";
import Chart from "react-google-charts";
import theme from "../../theme";
import Legend from "../shared/Legend";
import MetricsPanel from "../shared/MetricPanel/MetricsPanel";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TransactionUploader from "../Transactions/TransactionUpload/TransactionsUpload";
import frugalServicesClient from "../../utils/frugal-services-client";

const PREFIX = 'Dashboard';
const classes = {
    root: `${PREFIX}-root`,
    paper: `${PREFIX}-paper`,
    lineGraph: `${PREFIX}-lineGraph`,
    floatingAddBtn: `${PREFIX}-floatingAddBtn`
}
const Root = styled('div')(({ theme }) => ({
    [`&.${classes.root}`]: {
       display: "flex",
       paddingTop: '64px'
    },
    [`& .${classes.paper}`]: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
       backgroundColor: '#f5f5f5',
        width: '100%'
    },
    [`& .${classes.lineGraph}`]: {
        minHeight: "200px"
    },
   [`& .${classes.floatingAddBtn}`]: {
      padding: theme.spacing(2),
      display: 'flex',
      justifyContent: 'flex-end',
   },
}))


export default function Dashboard(props) {
  const {setPasswordExpired} = useContext(AuthContext);
  const {user, userLoading, selectedMonth, selectedYear, setSelectedMonth, setSelectedYear} = useContext(UserContext);
  const {accountCategories, accountFamily, loadingAccountCategories} = useContext(AccountContext);
  const [budgets, setBudgets] = React.useState([]);
  const [budgetsLoading, setBudgetsLoading] = React.useState(false);
  const [transactionDataLoading, setTransactionDataLoading] = useState(true);
  const [transactionData, setTransactionData] = useState([]);
  const [previousTransactionData, setPreviousTransactionData] = useState([]);
  const [spendAcrossCategoriesDataset, setSpendAcrossCategoriesDataset] = useState([]);
  const [sankeyData, setSankeyData] = useState(null);
  const [sankeyOptions, setSankeyOptions] = useState(null);
  const [openTransactionsUpload, setOpenTransactionsUpload] = useState(false);

  const [dataset, setDataset] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});

  // Transaction Input Modal
   const [openTransactionInputModal, setOpenTransactionInputModal] = React.useState(false);
   const handleOpenTransactionInputModal = () => setOpenTransactionInputModal(true);
   const handleCloseTransactionInputModal = () => setOpenTransactionInputModal(false);

   const handleOpenTransactionsUpload = () => setOpenTransactionsUpload(true);
   const handleCloseTransactionsUpload = () => setOpenTransactionsUpload(false);

   const handleMonthChange = e => { setSelectedMonth(e.target.value)}
   const handleYearChange = e => setSelectedYear(e.target.value);



   // Custom settings for line graph
   const customize = {
      height: 450,
      legend: { hidden: true },
      margin: { left: 65, right: 30, top: 25, bottom: 30 },
   };

  // Main total and per category line graph
   useEffect(() => {
      if(!transactionDataLoading && !budgetsLoading) {
         setDataset(formatters.generateTransactionDataset(transactionData, budgets));
      }
   }, [transactionData, accountCategories, budgets]);

  // A handy map of categories
   useEffect(() => {
      const map = accountCategories.reduce((acc, category) => {
         acc[category.id] = category;
         return acc;
      }, {});
      setCategoryMap(map);
   }, [accountCategories]);

   useEffect(() => {
      setTransactionDataLoading(false);
   }, [transactionData]);

  // Transaction data async load
   useEffect(() => {
      if(!userLoading) {
         (async () => {
            const response = await frugalServicesClient.getTransactions(selectedMonth, selectedYear);
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            const transactions = await response.json();
            setTransactionData(transactions);
         })();
      }
  }, [selectedMonth, selectedYear, userLoading, transactionDataLoading]);

  // Previous month transaction data
  useEffect(() => {
   if (!userLoading) {
     (async () => {
       const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
       const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
       const response = await frugalServicesClient.getTransactions(prevMonth, prevYear);
       if (response.status === 401) {
         setPasswordExpired(true);
       }
       const transactions = await response.json();
       setPreviousTransactionData(transactions);
     })();
   }
 }, [selectedMonth, selectedYear, userLoading]);

   // Category Budgets async load to populate total budget for graphs
   useEffect(() => {
      if(!userLoading) {
         (async () => {
            const response = await frugalServicesClient.getCategoryBudgets();
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            const budgets = await response.json();
            setBudgets(budgets);
            setBudgetsLoading(false);
         })();
      }
   }, [budgetsLoading, loadingAccountCategories, userLoading]);

   useEffect(() => {
      if(accountCategories.length > 0 && budgets.length > 0) {
        const chartData = generateDonutChartData(transactionData, accountCategories, calculateBudgetSum(budgets), categoryMap);
        setSpendAcrossCategoriesDataset(chartData);
      }
   }, [transactionData, accountCategories, budgets]);

   useEffect(() => {
      if (accountCategories.length > 0 && budgets.length > 0) {
         const data = generateSankeyData(transactionData, accountCategories, budgets);
         setSankeyData(data);
      }
   }, [transactionData, accountCategories, budgets]);

   useEffect(() => {
      if (sankeyData && accountCategories.length > 0) {
         const nodes = [...new Set(sankeyData.slice(1).flat().filter(v => typeof v === 'string'))];
         const categoryColorMap = accountCategories.reduce((acc, cat) => {
            acc[cat.category] = cat.color;
            return acc;
         }, {});

         const colors = nodes.map(node => {
            if (node === 'Total Budget') {
               return theme.palette.secondary.main;
            }
            return categoryColorMap[node] || '#a6a6a6';
         });

         setSankeyOptions({
            sankey: {
               node: {
                  colors: colors
               },
               link: {
                  colorMode: 'gradient',
               }
            }
         });
      }
   }, [sankeyData, accountCategories]);


   const dataForCategoryBarCharts = useMemo(() => {
      if(accountCategories.length > 0 && budgets.length > 0) {
         return createCategoryBarChartDatasets(transactionData, accountCategories, budgets);
      } else return [];
   }, [budgets, transactionData, accountCategories]);

   function calculateBudgetSum(budgets){
      const sum = budgets.reduce((accumulator, currentValue) => {
         return accumulator + currentValue.categoryBudget
      }, 0)
      return sum;
   }

   function getTotalSpendForCategory(transactions, categoryId) {
      const filteredTransactions = transactions.filter(transaction => {
         return transaction.category === categoryId;
      });

      const totalSpend = filteredTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
      return totalSpend;
   }

   function createCategoryBarChartDatasets(transactions, categories, categoryBudgets) {
      const datasets = [];

      categoryBudgets.forEach((budget) => {
         const categoryId = budget.categoryId;
         const budgetAmount = budget.categoryBudget;
         const fullCategory = categories.find(category => category.id === categoryId);
         const categoryName = fullCategory.category;
         const categorySpend = getTotalSpendForCategory(transactions, categoryId);

         const categoryBarChartData = {
            categoryId: categoryId,
            categoryName: categoryName,
            totalSpend: categorySpend,
            categoryBudget: budgetAmount
         }

         datasets.push(categoryBarChartData);
      });
      return datasets;
   }

   function generateSankeyData(transactions, categories, budgets) {
      const data = [
         ['From', 'To', 'Value'],
      ];

      const totalBudget = budgets.reduce((acc, b) => acc + b.categoryBudget, 0);
      const totalSpend = transactions.reduce((acc, t) => acc + t.amount, 0);
      const remainingBudget = totalBudget - totalSpend;

      if (totalBudget > 0) {
         data.push(['Total Budget', 'Total Spend', parseFloat(totalSpend.toFixed(2)) > 0 ? parseFloat(totalSpend.toFixed(2)) : 0]);
         if (remainingBudget > 0) {
            data.push(['Total Budget', 'Remaining', parseFloat(remainingBudget.toFixed(2))]);
         }
      }


      const spendByCat = {};
      transactions.forEach(t => {
         if (!spendByCat[t.category]) {
            spendByCat[t.category] = 0;
         }
         spendByCat[t.category] += t.amount;
      });

      const categoryMap = categories.reduce((acc, category) => {
         acc[category.id] = category.category;
         return acc;
      }, {});


      for (const catId in spendByCat) {
         const categoryName = categoryMap[catId] || 'Unknown Category';
         if (spendByCat[catId] > 0) {
            data.push(['Total Spend', categoryName, parseFloat(spendByCat[catId].toFixed(2))]);
         }
      }

      if (data.length > 1) {
         return data;
      }
      return null;
   }


   return (
      <Root className={classes.root}>
        <AppDrawer/>
         <Paper className={classes.paper}>
            <TransactionInput open={openTransactionInputModal} accountId={user?.accountId} serverUrl={appConfig.host} categories={accountCategories} handleClose={handleCloseTransactionInputModal} saveHandler={setTransactionDataLoading}/>
            <TransactionUploader open={openTransactionsUpload} accountId={user?.accountId} serverUrl={appConfig.host} categories={accountCategories} handleClose={handleCloseTransactionsUpload} importSuccessHandler={setTransactionDataLoading} />

             <Grid container alignItems="center" sx={{paddingBottom: "16px"}}>
                <Grid size={{ lg: 6, md: 6, sm: 12}}>
                   <DateYearSelection month={selectedMonth} year={selectedYear} handleMonthChange={handleMonthChange} handleYearChange={handleYearChange}/>
                </Grid>

                <Grid size={{xs: 6}} className={classes.floatingAddBtn}>
                  <Box sx={{ '& > :not(style)': { m: 1 } }}>
                     <MUITooltip title={"Add Transaction"}>
                        <Fab color="secondary" onClick={handleOpenTransactionInputModal}  aria-label="add">
                           <AddIcon />
                        </Fab>
                     </MUITooltip>
                     <MUITooltip title={"Upload Transactions"}>
                        <Fab color="primary" aria-label="upload" onClick={handleOpenTransactionsUpload}>
                           <UploadFileIcon />
                        </Fab>
                     </MUITooltip>
                  </Box>
                </Grid>
             </Grid>

            <Grid container spacing={2}>
               <Grid container size={{xs: 12, lg: 8}}>
                  <Grid size={{xs: 12, sm: 8}}>
                     <BudgetStatus budgets={budgets} transactions={transactionData}></BudgetStatus>
                  </Grid>

                  <Grid size={{xs:12, sm: 4}}>
                     <TransactionsByUser transactions={transactionData} accountUsers={accountFamily}></TransactionsByUser>
                  </Grid>

                  <Grid size={{xs:12}}>
                    <MetricsPanel 
                        transactionData={transactionData}
                        previousTransactionData={previousTransactionData}
                        budgets={budgets}
                        selectedMonth={selectedMonth}
                        selectedYear={selectedYear}
                        categoryMap={categoryMap}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                     <Paper className={classes.lineGraph} elevation={3}
                            sx={{
                               flexGrow: 1,
                               p: 2,
                               display: 'flex',
                               flexDirection: 'column',
                               gap: 1.5
                            }}>
                        {transactionDataLoading ? null: (
                           <Box sx={{alignItems: 'center', textAlign: 'center'}}>
                              <Typography variant={'h5'}>Category Budgets timeline</Typography>
                              <LineChart
                                 xAxis={[{
                                    scaleType: 'time',
                                    dataKey: 'date',
                                    valueFormatter: (value) => {
                                       return new Date(value).toLocaleDateString('en-US', {
                                          month: '2-digit',
                                          day: '2-digit',
                                       });
                                    },
                                 }]}
                                 yAxis={[{ valueFormatter: formatters.formatCurrency }]}
                                 series={[
                                    ...Object.keys(dataset[0] || {})
                                       .filter((key) => key !== 'date' && key !== 'total')
                                       .map((categoryId) => ({
                                          dataKey: categoryId,
                                          label: categoryMap[categoryId].category || categoryId,
                                          showMark: false,
                                          color: categoryMap[categoryId].color || formatters.getRandomChartColor(),
                                       })),
                                    {
                                       dataKey: 'total',
                                       label: 'Total Remaining Budget',
                                       showMark: true,
                                       color: '#4db6ac'
                                    },
                                 ]}
                                 dataset={dataset}
                                 {...customize}
                              />
                           </Box>

                        )}
                     </Paper>
                  </Grid>

                  {sankeyData && sankeyOptions && (
                     <Grid size={{xs: 12}}>
                        <Paper elevation={3} sx={{
                           minHeight: "480px",
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           padding: 2,
                           gap: 1.5
                        }}>
                           <Typography variant="h5" component="div" sx={{ mb: 1, mt: 1, textAlign: 'center'}}>
                              Money Flow
                           </Typography>
                           <Chart
                              chartType="Sankey"
                              width="100%"
                              height="400px"
                              data={sankeyData}
                              options={sankeyOptions}
                           />
                        </Paper>
                     </Grid>
                  )}

                  <Grid size={{xs: 12}}>
                     <Paper elevation={3} sx={{
                        minHeight: "480px",
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                        gap: 1.5
                     }}>
                        <Typography variant="h5" component="div" sx={{ mb: 1, mt: 1, textAlign: 'center'}}>
                           Spend Across Categories
                        </Typography>
                        <PieChart
                           series={[
                              {
                                 innerRadius: 40,
                                 data: spendAcrossCategoriesDataset,
                              },
                           ]}
                           slotProps={{
                              legend: { hidden: true },
                           }}
                        />
                     </Paper>
                  </Grid>
               </Grid>

               <Grid container size={{xs: 12, lg: 4}}>
                  <Paper elevation={3} sx={{
                     padding: 2,
                  }}>
                     <Grid><Typography sx={{ textAlign: 'center', marginTop: '16px' }} gutterBottom variant="h5">Category Budgets Overview</Typography></Grid>
                     <Legend items={[
                        { color: '#4db6ac', label: 'Spend' },
                        { color: '#FF6961', label: 'Overage' },
                     ]} />
                     <Grid container spacing={2}>
                        {dataForCategoryBarCharts.length > 0 ? (
                           dataForCategoryBarCharts.map((categoryData) => (
                              <Grid size={{xs:12, sm: 6}} key={categoryData.categoryId}>
                                 <CategorySpendBarChart
                                    categoryId={categoryData.categoryId}
                                    categoryName={categoryData.categoryName}
                                    totalSpend={categoryData.totalSpend}
                                    categoryBudget={categoryData.categoryBudget}
                                 />
                              </Grid>
                           ))
                        ) : (
                           <Grid>
                              <Typography>No budget data available.</Typography>
                           </Grid>
                        )}
                     </Grid>
                  </Paper>
               </Grid>
            </Grid>


            <div style={{ padding: 15}}></div>
         </Paper>
      </Root>
    );

  function generateDonutChartData(transactions, accountCategories, totalBudget, categoryMap) {
    const categoryTotals = {};

    if(transactions.length > 0) {
       transactions.forEach(transaction => {
          const categoryId = transaction.category;
          const transactionAmount = transaction.amount;

          if (categoryTotals[categoryId] === undefined) {
             categoryTotals[categoryId] = 0;
          }

          categoryTotals[categoryId] += transactionAmount;
       });
    }

    const chartData = []

    if(Object.keys(categoryTotals).length === 0) {
       chartData.push(
          {
             id: 0, value: totalBudget,
             label: "You've spent nothing!",
             color: formatters.getRandomChartColor()
          });
    } else {
       Object.keys(categoryTotals).forEach(categoryId => {
          const categoryName = accountCategories.find(category => category.id === categoryId).category;
          const spending = categoryTotals[categoryId];
          chartData.push(
             {
                id: categoryId,
                value: spending,
                label: categoryName,
                color: categoryMap[categoryId].color || formatters.getRandomChartColor()
             });
       });
    }
    return chartData;
  }
}